import {
    Column,
    Container,
    colorWithOpacity,
    cssVar,
    Grid,
    Header,
    Label,
    Link,
    Row,
    Separator,
    useAlignments,
    useScreen,
    useSizes,
} from 'metrix-common3';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import BoardColumn from './Column';

const Board = (props) => {
    const navigate = useNavigate();
    const [currentColumn, setCurrentColumn] = useState(0);
    const intl = useIntl();
    const alignments = useAlignments();
    const sizes = useSizes();
    const screen = useScreen();

    // biome-ignore lint/correctness/useExhaustiveDependencies: mount-only init
    useEffect(() => {
        props.doReduceView('board');
        props.doGetBoardPolicies();
    }, []);

    // Define status columns with internationalized names
    const statusColumns = [
        {
            name: intl.formatMessage({ id: 'Policies_Board.status.submitted' }),
            status: ['Submitted'],
        },
        {
            name: intl.formatMessage({ id: 'Policies_Board.status.quoteIssued' }),
            status: ['Quoted', 'Quote Issued'],
        },
        {
            name: intl.formatMessage({ id: 'Policies_Board.status.quoteAccepted' }),
            status: ['Quote Accepted'],
        },
        {
            name: intl.formatMessage({ id: 'Policies_Board.status.bindRequested' }),
            status: ['Bind Requested'],
        },
    ];

    // Define swimlanes with internationalized values
    const swimlanes = [
        {
            code: 'new',
            value: intl.formatMessage({ id: 'Policies_Board.type.newBusiness' }),
        },
    ];

    const handleAssignment = (guid, assignedTo) => {
        props.doExecuteAssignmentAction({ guid, assignedTo });
        props.doGetBoardPolicies();
    };

    const getSwimlane = (priorityCategory, column, displayPolicies) => {
        const policies = priorityCategory
            ? displayPolicies?.filter(
                  (x) => column.status.includes(x.transactionStatus) && x.priorityCategory === priorityCategory,
              )
            : displayPolicies?.filter((x) => column.status.includes(x.transactionStatus) && !x.priorityCategory);
        return (
            <Column
                xs={screen?.isMobile ? 12 : 12 / statusColumns.length}
                key={`${priorityCategory}${column.name}Column`}
                nogutter
            >
                <BoardColumn
                    name={column.name}
                    status={column.status}
                    policies={policies}
                    key={column.status}
                    reference={props.reference}
                    navigate={navigate}
                    assignmentEvent={handleAssignment}
                    columnCount={statusColumns.length}
                    doExecuteAssignmentAction={props.doExecuteAssignmentAction}
                    context={props.context}
                    products={props.products}
                    intl={intl}
                />
            </Column>
        );
    };

    const handlePreviousColumn = () => {
        if (currentColumn === 0) {
            setCurrentColumn(statusColumns.length - 1);
        } else {
            setCurrentColumn(currentColumn - 1);
        }
    };

    const handleNextColumn = () => {
        if (currentColumn === statusColumns.length - 1) {
            setCurrentColumn(0);
        } else {
            setCurrentColumn(currentColumn + 1);
        }
    };

    const getSwimlaneColor = (swimlane) => {
        let color = null;

        switch (swimlane.code) {
            case 'new':
                color = colorWithOpacity(cssVar.colorPrimary, cssVar.opacityVeryLight);
                break;
            case 'low':
                color = colorWithOpacity(cssVar.colorTertiary, cssVar.opacityVeryLight);
                break;
            case 'none':
                color = colorWithOpacity(cssVar.colorStrokeMedium, cssVar.opacityLight);
                break;
        }

        return color;
    };

    const query = props.query.board;
    const policyType = query.transactionType;
    const broker = query.brokerReferenceGuid;
    const brokerage = query.brokerCompanyReferenceGuid;

    const policies = props.boardPolicies;
    let displayPolicies = [];
    let displaySwimlane = [];

    // Filter Transaction Type
    if (policyType === 'new') {
        displayPolicies = policies?.filter((policy) => policy.transactionType === 'New Business');
        displaySwimlane = swimlanes?.filter((lane) => lane.code === 'new');
    } else {
        displayPolicies = policies;
        displaySwimlane = swimlanes;
    }

    // Filter Broker or Brokerage
    if (broker) {
        displayPolicies = displayPolicies?.filter((policy) => policy.brokerReferenceGuid === broker);
    }
    if (brokerage) {
        displayPolicies = displayPolicies?.filter((policy) => policy.brokerCompanyReferenceGuid === brokerage);
    }

    let columns = statusColumns;
    const noOfColumns = columns.length;
    if (screen?.isMobile) {
        columns = [columns[currentColumn]];
    }

    // Get None text for empty value
    const noneText = intl.formatMessage({ id: 'Policies_Board.type.none' });

    // Get navigation text
    const prevText = intl.formatMessage({ id: 'Policies_Board.navigation.previous' });
    const nextText = intl.formatMessage({ id: 'Policies_Board.navigation.next' });

    return (
        <Container>
            <Grid>
                <Row>
                    {columns.map((column) => {
                        return screen?.isMobile ? (
                            <Column xs={screen?.isMobile ? 12 : 12 / noOfColumns} key={Math.random().toString()}>
                                <Container
                                    fullSize
                                    inline
                                    horizontalAlignment={screen?.isMobile ? alignments.center : alignments.left}
                                >
                                    {noOfColumns > 1 && (
                                        <Link text={prevText} right={sizes.xs} clickEvent={handlePreviousColumn} />
                                    )}
                                    <Label
                                        bold
                                        textAlign={screen?.isMobile ? alignments.center : alignments.left}
                                        text={`${column.name} (${displayPolicies.filter((x) => column.status.includes(x.transactionStatus)).length || 0})`}
                                    />
                                    {noOfColumns > 1 && (
                                        <Link text={nextText} left={sizes.xs} clickEvent={handleNextColumn} />
                                    )}
                                </Container>
                                <Separator solid bottom={sizes.xxs} />
                            </Column>
                        ) : (
                            <Column xs={12 / noOfColumns} key={Math.random().toString()}>
                                <Header
                                    size={sizes.xxs}
                                    textAlign={alignments.left}
                                    text={`${column.name} (${displayPolicies.filter((x) => column.status.includes(x.transactionStatus)).length || 0})`}
                                />
                                <Separator solid bottom={sizes.xxs} />
                            </Column>
                        );
                    })}
                </Row>
                {displaySwimlane.map((swimlane) => {
                    return (
                        <Row nogutter key={Math.random().toString()}>
                            <Column nogutter>
                                <Container
                                    color={getSwimlaneColor(swimlane)}
                                    top={sizes.xs}
                                    bottom={sizes.xs}
                                    left={sizes.xxs}
                                    right={sizes.xxs}
                                >
                                    <Label text={swimlane.value || noneText} top={sizes.xxs} bottom={sizes.xxs} />
                                </Container>
                            </Column>
                            {columns.map((column) => {
                                return getSwimlane(swimlane.value, column, displayPolicies);
                            })}
                        </Row>
                    );
                })}
            </Grid>
        </Container>
    );
};

export default Board;
